import CommonApi from "../common/CommonApi";
import axios from "axios";
import SuccessResponse from "../dto/common/SuccessResponse";
import RevenueAnalysisDto from "../dto/revenue/RevenueAnalysisDto";
import RevenueShopDto from "../dto/revenue/RevenueShopDto";
import RevenueDayDto from "../dto/revenue/RevenueDayDto";
import RevenueTotalDto from "../dto/revenue/RevenueTotalDto";
import RevenueOrderListDto from "../dto/revenue/RevenueOrderListDto";
import CommonRequestBodyDto from "../dto/common/CommonRequestBodyDto";
import RevenueVoidReqDto from "../dto/revenue/RevenueVoidReqDto";
import RevenueVoidRespDto from "../dto/revenue/RevenueVoidRespDto";
import RevenueVoidSumDto from "../dto/revenue/RevenueVoidSumDto";
import IndiaTransactionReqDto from "../dto/revenue/IndiaTransactionReqDto";
import IndiaReportDto from "../dto/revenue/IndiaReportDto";
import UsageDayDto from "../dto/revenue/UsageDayDto";
import UsageWeekDto from "../dto/revenue/UsageWeekDto";
import UsageHourWeekDto from "../dto/revenue/UsageHourWeekDto";
import UsageShopDto from "../dto/revenue/UsageShopDto";
import UsageDeviceDto from "../dto/revenue/UsageDeviceDto";

type DateRange = { startDate: string; endDate: string };

class RealRevenueApi extends CommonApi {
    async getRevenueAnalysis({
        startDate,
        endDate,
    }: DateRange): Promise<SuccessResponse<RevenueAnalysisDto[]>> {
        const baseHeaders = this.makeBaseHeader();

        await axios.options(`${this.BASE_SERVICE_URL}/revenue/analysis`, {
            headers: baseHeaders,
        });

        const response = await axios.get<SuccessResponse<RevenueAnalysisDto[]>>(
            `${this.BASE_SERVICE_URL}/revenue/analysis`,
            {
                headers: baseHeaders,
                params: { startDate, endDate },
            }
        );

        return response.data;
    }

    async getRevenueShop({
        startDate,
        endDate,
    }: DateRange): Promise<SuccessResponse<RevenueShopDto[]>> {
        const baseHeaders = this.makeBaseHeader();

        await axios.options(`${this.BASE_SERVICE_URL}/revenue/shop`, {
            headers: baseHeaders,
        });

        const response = await axios.get<SuccessResponse<RevenueShopDto[]>>(
            `${this.BASE_SERVICE_URL}/revenue/shop`,
            {
                headers: baseHeaders,
                params: { startDate, endDate },
            }
        );

        return response.data;
    }

    async getRevenueDay({
        startDate,
        endDate,
    }: DateRange): Promise<SuccessResponse<RevenueDayDto[]>> {
        const baseHeaders = this.makeBaseHeader();

        await axios.options(`${this.BASE_SERVICE_URL}/revenue/day`, {
            headers: baseHeaders,
        });

        const response = await axios.get<SuccessResponse<RevenueDayDto[]>>(
            `${this.BASE_SERVICE_URL}/revenue/day`,
            {
                headers: baseHeaders,
                params: { startDate, endDate },
            }
        );

        return response.data;
    }

    async getRevenueTotal({
        startDate,
        endDate,
    }: DateRange): Promise<SuccessResponse<RevenueTotalDto[]>> {
        const baseHeaders = this.makeBaseHeader();

        await axios.options(`${this.BASE_SERVICE_URL}/revenue/total`, {
            headers: baseHeaders,
        });

        const response = await axios.get<SuccessResponse<RevenueTotalDto[]>>(
            `${this.BASE_SERVICE_URL}/revenue/total`,
            {
                headers: baseHeaders,
                params: { startDate, endDate },
            }
        );

        return response.data;
    }

    async getRevenueOrderList({
        orderId,
        shopCode,
        shopName,
        deviceName,
        startDate,
        endDate,
        result,
        page,
        pageSize,
    }: {
        orderId?: string;
        shopCode?: string;
        shopName?: string;
        deviceName?: string;
        startDate: string;
        endDate: string;
        result?: string;
        page: number;
        pageSize: number;
    }): Promise<SuccessResponse<RevenueOrderListDto>> {
        const baseHeaders = this.makeBaseHeader();

        const query: Record<string, any> = {
            startDate,
            endDate,
            page,
            pageSize,
        };
        if (orderId) query.orderId = orderId;
        if (shopCode) query.shopCode = shopCode;
        if (shopName) query.shopName = shopName;
        if (deviceName) query.deviceName = deviceName;
        if (result) query.result = result;

        await axios.options(`${this.BASE_SERVICE_URL}/revenue/orders`, {
            headers: baseHeaders,
        });

        const response = await axios.get<SuccessResponse<RevenueOrderListDto>>(
            `${this.BASE_SERVICE_URL}/revenue/orders`,
            {
                headers: baseHeaders,
                params: query,
            }
        );

        return response.data;
    }

    async saveRevenueVoid(
        reqDto: CommonRequestBodyDto<RevenueVoidReqDto>
    ): Promise<SuccessResponse<RevenueVoidRespDto>> {
        const baseHeaders = this.makeBaseHeader();

        await axios.options(`${this.BASE_SERVICE_URL}/revenue/void`, {
            headers: baseHeaders,
        });

        const response = await axios.post<SuccessResponse<RevenueVoidRespDto>>(
            `${this.BASE_SERVICE_URL}/revenue/void`,
            reqDto,
            {
                headers: baseHeaders,
            }
        );

        return response.data;
    }

    async getRevenueVoid({
        startDate,
        endDate,
    }: DateRange): Promise<SuccessResponse<RevenueVoidSumDto>> {
        const baseHeaders = this.makeBaseHeader();

        await axios.options(`${this.BASE_SERVICE_URL}/revenue/void`, {
            headers: baseHeaders,
        });

        const response = await axios.get<SuccessResponse<RevenueVoidSumDto>>(
            `${this.BASE_SERVICE_URL}/revenue/void`,
            {
                headers: baseHeaders,
                params: { startDate, endDate },
            }
        );

        return response.data;
    }

    async uploadIndiaTransaction(
        reqDto: CommonRequestBodyDto<IndiaTransactionReqDto>
    ): Promise<SuccessResponse<object>> {
        const baseHeaders = this.makeBaseHeader();

        await axios.options(`${this.BASE_SERVICE_URL}/revenue/india/report/transaction`, {
            headers: baseHeaders,
        });

        const response = await axios.post<SuccessResponse<object>>(
            `${this.BASE_SERVICE_URL}/revenue/india/report/transaction`,
            reqDto,
            {
                headers: baseHeaders,
            }
        );

        return response.data;
    }

    async getIndiaReport(params: {
        transactionId?: string;
        shopCode?: string;
        shopName?: string;
        deviceName?: string;
        startday: string;
        endday: string;
        result: string;
        ordertype?: string;
    }): Promise<SuccessResponse<IndiaReportDto[]>> {
        const baseHeaders = this.makeBaseHeader();

        const query: Record<string, any> = {};
        Object.entries(params).forEach(([k, v]) => {
            if (v !== undefined && v !== "") query[k] = v;
        });

        await axios.options(`${this.BASE_SERVICE_URL}/revenue/india/report`, {
            headers: baseHeaders,
        });

        const response = await axios.get<SuccessResponse<IndiaReportDto[]>>(
            `${this.BASE_SERVICE_URL}/revenue/india/report`,
            {
                headers: baseHeaders,
                params: query,
            }
        );

        return response.data;
    }

    async getUsageDay({
        startDate,
        endDate,
    }: DateRange): Promise<SuccessResponse<UsageDayDto[]>> {
        const baseHeaders = this.makeBaseHeader();

        await axios.options(`${this.BASE_SERVICE_URL}/revenue/analysis/usage/day`, {
            headers: baseHeaders,
        });

        const response = await axios.get<SuccessResponse<UsageDayDto[]>>(
            `${this.BASE_SERVICE_URL}/revenue/analysis/usage/day`,
            {
                headers: baseHeaders,
                params: { startDate, endDate },
            }
        );

        return response.data;
    }

    async getUsageWeek({
        startDate,
        endDate,
    }: DateRange): Promise<SuccessResponse<UsageWeekDto[]>> {
        const baseHeaders = this.makeBaseHeader();

        await axios.options(`${this.BASE_SERVICE_URL}/revenue/analysis/usage/week`, {
            headers: baseHeaders,
        });

        const response = await axios.get<SuccessResponse<UsageWeekDto[]>>(
            `${this.BASE_SERVICE_URL}/revenue/analysis/usage/week`,
            {
                headers: baseHeaders,
                params: { startDate, endDate },
            }
        );

        return response.data;
    }

    async getUsageHourWeek({
        startDate,
        endDate,
    }: DateRange): Promise<SuccessResponse<UsageHourWeekDto[]>> {
        const baseHeaders = this.makeBaseHeader();

        await axios.options(`${this.BASE_SERVICE_URL}/revenue/analysis/usage/hour-week`, {
            headers: baseHeaders,
        });

        const response = await axios.get<SuccessResponse<UsageHourWeekDto[]>>(
            `${this.BASE_SERVICE_URL}/revenue/analysis/usage/hour-week`,
            {
                headers: baseHeaders,
                params: { startDate, endDate },
            }
        );

        return response.data;
    }

    async getUsageShop({
        startDate,
        endDate,
    }: DateRange): Promise<SuccessResponse<UsageShopDto[]>> {
        const baseHeaders = this.makeBaseHeader();

        await axios.options(`${this.BASE_SERVICE_URL}/revenue/analysis/usage/shop`, {
            headers: baseHeaders,
        });

        const response = await axios.get<SuccessResponse<UsageShopDto[]>>(
            `${this.BASE_SERVICE_URL}/revenue/analysis/usage/shop`,
            {
                headers: baseHeaders,
                params: { startDate, endDate },
            }
        );

        return response.data;
    }

    async getUsageDevice({
        startDate,
        endDate,
    }: DateRange): Promise<SuccessResponse<UsageDeviceDto>> {
        const baseHeaders = this.makeBaseHeader();

        await axios.options(`${this.BASE_SERVICE_URL}/revenue/analysis/usage/device`, {
            headers: baseHeaders,
        });

        const response = await axios.get<SuccessResponse<UsageDeviceDto>>(
            `${this.BASE_SERVICE_URL}/revenue/analysis/usage/device`,
            {
                headers: baseHeaders,
                params: { startDate, endDate },
            }
        );

        return response.data;
    }

    async generateRevenueCumulative(): Promise<SuccessResponse<{ message: string }>> {
        const baseHeaders = this.makeBaseHeader();

        await axios.options(`${this.BASE_SERVICE_URL}/revenue/cumulative`, {
            headers: baseHeaders,
        });

        const response = await axios.put<SuccessResponse<{ message: string }>>(
            `${this.BASE_SERVICE_URL}/revenue/cumulative`,
            undefined,
            {
                headers: baseHeaders,
            }
        );

        return response.data;
    }
}

export default RealRevenueApi;
